import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import axiosUI from '../../axiosUI';
import { Link } from '@reach/router';
import { motion, AnimatePresence } from 'framer-motion';
import Sidebar from '../Sidebar/Sidebar';
import Topbar from '../Topbar/Topbar';
import Main from '../Main/Main';
import Button from '../Button/Button';
import PopupWrapper from '../PopupWrapper';
import Input, { INPUT_TYPES } from '../Input/Input';
import Loader from '../Loader/Loader';
import BannerChangesetRow from './BannerChangesetRow';
import { withGlobalContext } from '../../App';
import {
  NOTIFICATIONS,
  SALESCHANNEL_NAME_MAP,
  LOCALE_NAME_MAP,
} from '../../constants';

class BannerChangeset extends Component {
  static propTypes = {
    language: PropTypes.string,
    country: PropTypes.string,
  };

  constructor(props) {
    super(props);

    this.state = {
      drafts: [],
      selected: {},
      loading: false,
      showDiscardWarningPopup: false,
    };
  }

  componentDidMount = () => {
    this.setState({ loading: true }, async () => {
      try {
        const { data } = await axiosUI.get(
          `/api/banners/drafts?salesChannel=${this.props.country}&locale=${this.props.language}`
        );
        this.setState({
          drafts: data,
          selected: data.reduce((map, banner) => {
            map[banner.id] = true;
            return map;
          }, {}),
        });
      } catch (err) {
        console.log(err);
      }

      this.setState({ loading: false });
    });
  };

  toggleDiscardWarningPopup = () => {
    this.setState(prevState => ({
      showDiscardWarningPopup: !prevState.showDiscardWarningPopup,
    }));
  };

  discardChanges = async () => {
    const itemsToDelete = this.getSelectedItems();

    try {
      const response = await axiosUI.delete(`/api/banners/drafts`, {
        data: itemsToDelete,
      });
      const { deletedKeys } = response.data;
      const selected = { ...this.state.selected };
      deletedKeys.forEach(key => {
        delete selected[key];
      });

      this.setState(
        {
          drafts: this.state.drafts.filter(
            draft => !deletedKeys.includes(draft.id)
          ),
          selected,
        },
        () => {
          this.toggleDiscardWarningPopup();
        }
      );

      this.props.createNotification({
        message: `Changes are discarded`,
        type: NOTIFICATIONS.TYPE.INFO,
        timeout: 5000,
      });
    } catch (err) {
      console.log(err);
      this.toggleDiscardWarningPopup();

      this.props.createNotification({
        message: `Failed to discard changes`,
        type: NOTIFICATIONS.TYPE.ERROR,
        timeout: 3000,
      });
    }
  };

  approveChanges = () => {
    this.setState({ loading: true }, async () => {
      const itemsToApprove = this.getSelectedItems();

      try {
        await axiosUI.post(`/api/banners/approve`, itemsToApprove);

        // refetch drafts again to update table data;
        const { data } = await axiosUI.get(
          `/api/banners/drafts?salesChannel=${this.props.country}&locale=${this.props.language}`
        );
        this.setState({
          drafts: data,
          selected: data.reduce((map, banner) => {
            map[banner.id] = false;
            return map;
          }, {}),
        });

        this.props.createNotification({
          message: `Changes are approved`,
          type: NOTIFICATIONS.TYPE.INFO,
          timeout: 5000,
        });
      } catch (err) {
        console.log(err);

        this.props.createNotification({
          message: `Failed to approve changes`,
          type: NOTIFICATIONS.TYPE.ERROR,
          timeout: 3000,
        });
      }

      this.setState({ loading: false });
    });
  };

  publishChanges = () => {
    this.setState({ loading: true }, async () => {
      const itemsToPublish = this.getSelectedItems();

      try {
        await axiosUI.post(`/api/banners/publish`, itemsToPublish);

        // refetch drafts again to update table data;
        const { data } = await axiosUI.get(
          `/api/banners/drafts?salesChannel=${this.props.country}&locale=${this.props.language}`
        );
        this.setState({
          drafts: data,
          selected: data.reduce((map, banner) => {
            map[banner.id] = false;
            return map;
          }, {}),
        });

        this.props.createNotification({
          message: `Changes are published`,
          type: NOTIFICATIONS.TYPE.SUCCESS,
          timeout: 5000,
        });
      } catch (err) {
        console.log(err);

        this.props.createNotification({
          message: `Failed to publish changes`,
          type: NOTIFICATIONS.TYPE.ERROR,
          timeout: 3000,
        });
      }

      this.setState({ loading: false });
    });
  };

  toggleSelectAllRows = initialValue => {
    const selections = this.state.drafts.reduce((map, banner) => {
      map[banner.id] = !initialValue;
      return map;
    }, {});
    this.setState({ selected: selections });
  };

  toggleRowSelection = event => {
    this.setState({
      selected: {
        ...this.state.selected,
        [event.target.id]: event.target.checked,
      },
    });
  };

  getSelectedItems = () => {
    return this.state.drafts.filter(draft => this.state.selected[draft.id]);
  };

  render() {
    const selectedItems = this.getSelectedItems();
    const allAreSelected = selectedItems.length === this.state.drafts.length;
    const nonNullSelectedItems = Boolean(selectedItems.length);
    const canPublishSelectedChanges =
      nonNullSelectedItems &&
      selectedItems.every(
        draft => draft.first_approver && draft.second_approver
      );
    return (
      <Fragment>
        <Sidebar highlightNavItemIndex={2} />
        <Topbar className="banners_changeset__topbar">
          <span className="font-bold text-lg capitalize">
            <Link className="bb-1-blue no-underline text-blue" to="/banners">
              Banners
            </Link>{' '}
            /{' '}
            <Link
              className="bb-1-blue no-underline text-blue"
              to={`/banners/${this.props.country}/${this.props.language}`}>
              {SALESCHANNEL_NAME_MAP[this.props.country]} -{' '}
              {LOCALE_NAME_MAP[this.props.language]}
            </Link>{' '}
            / Review Changes
          </span>
          <span className="float-right text-right">
            <PopupWrapper
              className="relative"
              isOpen={this.state.showDiscardWarningPopup}
              togglePopup={this.toggleDiscardWarningPopup}>
              <Button
                type="red-transparent"
                className="h-8"
                disabled={!nonNullSelectedItems}
                onClick={this.toggleDiscardWarningPopup}>
                Discard Changes
              </Button>
              <AnimatePresence>
                {this.state.showDiscardWarningPopup && (
                  <motion.div
                    initial={{ opacity: 0, y: '80%' }}
                    animate={{ opacity: 1, y: '100%' }}
                    exit={{ opacity: 0, y: '80%' }}
                    transition={{
                      ease: [0.18, 0.89, 0.39, 1.4],
                      duration: 0.3,
                    }}
                    className="button-popup absolute pin-r px-2 py-4 bg-white shadow-md rounded text-left w-64">
                    <div className="text-center leading-normal">
                      Are you sure you want to discard selected changes?
                    </div>
                    <div className="flex justify-around mt-8">
                      <Button
                        className="h-8"
                        onClick={this.toggleDiscardWarningPopup}>
                        No
                      </Button>
                      <Button
                        type="red-transparent"
                        className="h-8"
                        onClick={this.discardChanges}>
                        Yes, discard them
                      </Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </PopupWrapper>
            <Button
              type="blue"
              className="h-8 h-8 ml-8"
              disabled={!nonNullSelectedItems}
              onClick={this.approveChanges}>
              Approve Changes
            </Button>
            <Button
              type="green"
              className="h-8 h-8 ml-8"
              onClick={this.publishChanges}
              disabled={!canPublishSelectedChanges}>
              Publish Changes
            </Button>
          </span>
        </Topbar>
        <Main className="banners_changeset__main relative">
          <Loader loading={this.state.loading}>
            <div className="inline-flex items-center bg-white p-2 rounded mb-4">
              <div className="w-4 h-4 bg-lightGreen rounded-full mr-2" />
              <div className="mr-6 text-sm">Additions</div>
              <div className="w-4 h-4 bg-lightYellow rounded-full mr-2" />
              <div className="mr-6 text-sm">Modifications</div>
              <div className="w-4 h-4 bg-lightRed rounded-full mr-2" />
              <div className="text-sm">Deletions</div>
            </div>
            <table className="text-left w-full border-collapse bg-white">
              <thead>
                <tr>
                  <th className="p-2">
                    <Input
                      id="select_all"
                      name="select_all"
                      type={INPUT_TYPES.CHECKBOX}
                      onChange={() => this.toggleSelectAllRows(allAreSelected)}
                      checked={allAreSelected}
                      inputClassName="box_checkbox"
                    />
                  </th>
                  <th className="p-2 font-normal">Updated By</th>
                  <th className="p-2 font-normal">Updated On</th>
                  <th className="p-2 font-normal max-w-xs">Banner</th>
                  <th className="p-2 w-16 font-normal text-right">Approvals</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence initial={false}>
                  {this.state.drafts.map(draft => {
                    let totalApprovals = 0;
                    if (draft.first_approver && draft.first_approver.trim()) {
                      totalApprovals += 1;
                    }
                    if (draft.second_approver && draft.second_approver.trim()) {
                      totalApprovals += 1;
                    }
                    return (
                      <BannerChangesetRow
                        key={draft.id}
                        draft={draft}
                        selected={this.state.selected}
                        toggleRowSelection={this.toggleRowSelection}
                        totalApprovals={totalApprovals}
                      />
                    );
                  })}
                </AnimatePresence>
              </tbody>
            </table>
          </Loader>
        </Main>
      </Fragment>
    );
  }
}

export default  withGlobalContext(BannerChangeset);
